import React from 'react';
import { useLang } from '../../util/lang';
import styles from '../RestorativePhaseCaseIntake.module.css';
import shadeSummaryStyles from './PrescriptionNoteStep.module.css';
import { useSelector } from 'react-redux';

const FIT_VALUES = ['option.formaInjectionTechnique', 'option.flowableInjectionTechnique'];

const LANG_PREFIX = '@PatientInfo.smileDesign.restorativePhase';
const LAYER_LABEL_KEYS = {
    enamelOrDentin: `${LANG_PREFIX}.layerEnamelOrDentin`,
    enamel: `${LANG_PREFIX}.layerEnamel`,
    dentin: `${LANG_PREFIX}.layerDentin`,
};

const SHADE_GUIDES = [
    { group: `${LANG_PREFIX}.standardShades`, guide: `${LANG_PREFIX}.gcBleachShadeGuide`, options: ['XBW', 'BW'] },
    { group: `${LANG_PREFIX}.standardShades`, guide: `${LANG_PREFIX}.vitaClassicShadeGuide`, options: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2'] },
    { group: `${LANG_PREFIX}.opaqueShades`, options: ['AO1', 'AO2', 'AO3'] },
    { group: `${LANG_PREFIX}.translucentShades`, options: ['JE', 'AE'] },
];

const OPTION_KEYS = {
    preOrthodontic: 'preOrthodontic',
    postOrthodontic: 'postOrthodontic',
    wear: 'fitWear',
    veneers: 'fitVeneers',
    oneLayer: 'oneLayer',
    twoLayers: 'twoLayers',
    zirconia: 'zirconia',
    lithiumDisilicate: 'lithiumDisilicate',
    zirneer: 'zirneer',
    ezneer: 'ezneer',
    ipsEmax: 'ipsEmax',
    ipsEmaxCad: 'ipsEmaxCad',
    econLithiumDisilicate: 'econLithiumDisilicate',
    otherZirconia: 'others',
    otherLithiumDisilicate: 'others',
    layered: 'ipsEmaxLayered',
    low: 'low',
    medium: 'medium',
    high: 'high',
    smooth: 'smooth',
    pronounced: 'pronounced',
    fullGlazing: 'fullGlazing',
    stainingGlazing: 'stainingGlazing',
    yes: 'yes',
    no: 'no',
    'Masking Liner': 'maskingLiner',
    'Increased Cervical Chroma / Saturation': 'increasedCervicalChroma',
    'White Spots': 'whiteSpots',
    'Craze Lines / Crack-Line Effects': 'crazeLines',
    'Incisal Translucency': 'incisalTranslucency',
    'Opalescence Effects': 'opalescenceEffects',
    'Halo Effects': 'haloEffects',
    'Other custom characterization': 'otherCharacterization',
    'Bleaching retainer': 'bleachingRetainer',
    'Ecodont combi retainer': 'ecodontCombiRetainer',
    'Ecodont® Retainer with Veneer Shield': 'veneerRetainer',
    'Veneer retainer': 'veneerRetainer',
    'Ecodont retainer': 'ecodontRetainer',
    'Invisible retainer': 'invisibleRetainer',
    Splint: 'splint',
};

/** Translates a stored option value while retaining technical shade codes unchanged. */
const getOptionLabel = (value, get) => {
    const key = OPTION_KEYS[value];
    return key ? get(`@PatientInfo.smileDesign.restorativePhase.options.${key}`) : value;
};

/** Returns the guide metadata for a FIT shade option. */
const getShadeGuide = (option) =>
    SHADE_GUIDES.find((guide) => guide.options.includes(option)) ||
    (option === 'Masking Liner'
        ? { group: `${LANG_PREFIX}.options.maskingLiner`, options: [option] }
        : { group: `${LANG_PREFIX}.otherShades`, options: [option] });

/** Groups stored FIT shades for a guide-specific read-only summary. */
const groupFitShades = (value) => {
    const shadeValues = Array.isArray(value) ? value : value ? [value] : [];

    return shadeValues.reduce((groups, shade) => {
        const item = typeof shade === 'object' ? shade : { option: shade };
        if (!item.option) return groups;

        const guide = getShadeGuide(item.option);
        const group = groups.find((entry) => entry.name === guide.group) || {
            name: guide.group,
            guides: [],
        };
        const guideEntry = group.guides.find((entry) => entry.name === guide.guide) || {
            name: guide.guide,
            items: [],
        };

        if (!groups.includes(group)) groups.push(group);
        if (!group.guides.includes(guideEntry)) group.guides.push(guideEntry);
        guideEntry.items.push(item);
        return groups;
    }, []);
};

/** Renders a guide-specific read-only summary for FIT shade selections. */
const FitShadeSummary = ({ value, showLayer = false, showQuantity = false, get }) => {
    const languageGet = useLang();
    const translate = get || languageGet;
    const groups = groupFitShades(value);
    if (!groups.length) return <div className="restorative-readonly-value">-</div>;

    return (
        <div className={shadeSummaryStyles.shadeSummary}>
            {groups.map((group) => (
                <section className={shadeSummaryStyles.shadeGroup} key={group.name}>
                    <div className={shadeSummaryStyles.shadeGroupTitle}>{translate(group.name)}</div>
                    {group.guides.map((guide) => (
                        <div className={shadeSummaryStyles.shadeGuide} key={guide.name || group.name}>
                            {guide.name && <div className={shadeSummaryStyles.shadeGuideTitle}>{translate(guide.name)}</div>}
                            <div className={shadeSummaryStyles.shadeItems}>
                                {guide.items.map((item, index) => (
                                    <span className={shadeSummaryStyles.shadeItem} key={`${item.option}-${index}`}>
                                        {showLayer && item.layer && (
                                            <span
                                                className={`${shadeSummaryStyles.shadeLayer} ${shadeSummaryStyles[item.layer] || ''}`}
                                            />
                                        )}
                                        {item.option}
                                        {showLayer && item.layer && LAYER_LABEL_KEYS[item.layer] && ` · ${translate(LAYER_LABEL_KEYS[item.layer])}`}
                                        {showQuantity && item.quantity !== undefined && ` × ${item.quantity}`}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>
            ))}
        </div>
    );
};

/** Formats an answer for a read-only restorative phase question. */
const formatValue = (value, get) => {
    if (Array.isArray(value)) {
        return (
            value
                .map((item) => {
                    if (typeof item === 'object' && item.quantity !== undefined)
                        return `${getOptionLabel(item.option, get)} × ${item.quantity}`;
                    if (typeof item === 'object' && item.option) return getOptionLabel(item.option, get);
                    return getOptionLabel(item, get);
                })
                .join(', ') || '-'
        );
    }
    if (typeof value === 'object' && value.option) return getOptionLabel(value.option, get);
    return value ? getOptionLabel(value, get) : '-';
};

/** Renders one read-only restorative phase question and its submitted value. */
const ReadOnlyQuestion = ({ number, title, value, get }) => (
    <div>
        <div className="restorative-question-title">{number && `${number}. `}{title}</div>
        <div className="restorative-readonly-value">{formatValue(value, get)}</div>
    </div>
);

/** Renders restoration detail questions using the same branch conditions as the editable form. */
const PrescriptionReadOnlyDetails = ({ data, get }) => {
    const solution = data.plannedRestorationSolution;
    const isFit = FIT_VALUES.includes(solution);
    const isCeramic = solution === 'option.ceramicVeneersCrowns';
    const isGuided = solution === 'option.guidedVeneerTreatment';
    const material = data.materialPreference;
    const shadeGuideLabel =
        {
            vita: get('@PatientInfo.smileDesign.restorativePhase.vitaShadeGuide'),
            ezneer: get('@PatientInfo.smileDesign.restorativePhase.ezneerShadeGuide'),
            ipsEmax: get('@PatientInfo.smileDesign.restorativePhase.ipsEmaxShadeGuide'),
        }[data.shadeGuide] || '-';
    const materialPreferenceLabel = data.subMaterialPreference
        ? `${formatValue(material, get)} - ${formatValue(data.subMaterialPreference, get)}${
              data.specifiedMaterialIfAny ? ` (${formatValue(data.specifiedMaterialIfAny, get)})` : ''
          }`
        : formatValue(material, get);

    if (!isFit && !isCeramic && !isGuided) return null;

    return (
        <>
            <div className="restorative-section-heading">{get('@PatientInfo.smileDesign.restorativePhase.sectionTwo')}</div>
            {isFit && (
                <>
                    <div className="restorative-product-heading">
                        F.I.T. — {get(
                            solution === 'option.formaInjectionTechnique'
                                ? '@PatientInfo.smileDesign.prescriptionNotes.option.formaInjectionTechnique'
                                : '@PatientInfo.smileDesign.prescriptionNotes.option.flowableInjectionTechnique'
                        )}
                    </div>
                    {/* <ReadOnlyQuestion number={3} title={get('@PatientInfo.smileDesign.restorativePhase.orthodonticPhase')} value={data.orthodonticPhase} get={get} />
                    <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.fitType')} value={data.fitType} get={get} /> */}
                    <ReadOnlyQuestion number={2} title={get('@PatientInfo.smileDesign.restorativePhase.injectionLayers')} value={data.injectionLayers} get={get} />
                    <div>
                        <div className="restorative-question-title">3. {get('@PatientInfo.smileDesign.restorativePhase.fitShade')}</div>
                        <FitShadeSummary value={data.nanoFilledInjectableCompositeShade} showLayer />
                    </div>
                    <div>
                        <div className="restorative-question-title">
                            4. {get('@PatientInfo.smileDesign.restorativePhase.additionalSyringes')}{' '}
                            <span>({get('@PatientInfo.smileDesign.restorativePhase.additionalSyringesOptional')})</span>
                        </div>
                        <FitShadeSummary value={data.additionalSyringesIncreasedOpacity} showQuantity />
                    </div>
                    <ReadOnlyQuestion number={5} title={get('@PatientInfo.smileDesign.restorativePhase.optionalAppliances')} value={data.optionalAppliances} get={get} />
                    <ReadOnlyQuestion number={6} title={get('@PatientInfo.smileDesign.restorativePhase.remark')} value={data.restorationRemark} get={get} />
                </>
            )}
            {isCeramic && (
                <>
                    <div className="restorative-product-heading">
                        {get('@PatientInfo.smileDesign.restorativePhase.ceramicVeneersManualDesc')}
                    </div>
                    <ReadOnlyQuestion number={2} title={get('@PatientInfo.smileDesign.restorativePhase.materialPreference')} value={materialPreferenceLabel} get={get} />
                    <div className="restorative-question-title">3. {get('@PatientInfo.smileDesign.restorativePhase.shade')}</div>
                    <div className="restorative-readonly-value">{shadeGuideLabel}</div>
                    <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.prepStumpShade')} value={data.currentShade} get={get} />
                    <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.targetShade')} value={data.targetShade} get={get} />
                    {data.shadeGuide === 'ezneer' && <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.materialSelection')} value={data.materialSelection} get={get} />}
                    {data.shadeGuide === 'vita' && data.otherShade && <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.options.others')} value={data.otherShade} get={get} />}
                    <ReadOnlyQuestion number={4} title={get('@PatientInfo.smileDesign.restorativePhase.translucencyLevel')} value={data.translucencyLevel} get={get} />
                    <ReadOnlyQuestion number={5} title={get('@PatientInfo.smileDesign.restorativePhase.surfaceTexture')} value={data.surfaceTexture} get={get} />
                    <ReadOnlyQuestion number={6} title={get('@PatientInfo.smileDesign.restorativePhase.surfaceFinishing')} value={data.surfaceFinishingCharacterization} get={get} />
                    {data.surfaceFinishingCharacterization === 'stainingGlazing' && <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.effectsToReproduce')} value={data.effectsToReproduce} get={get} />}
                    <ReadOnlyQuestion
                        number={7}
                        title={
                            <>
                                {get('@PatientInfo.smileDesign.restorativePhase.cementationTray')}{' '}
                                <span>({get('@PatientInfo.smileDesign.restorativePhase.cementationTrayOptional')})</span>
                            </>
                        }
                        value={data.cementationTray}
                        get={get}
                    />
                    <ReadOnlyQuestion number={8} title={get('@PatientInfo.smileDesign.restorativePhase.designSpecifications')} value={data.designSpecifications} get={get} />
                    <ReadOnlyQuestion number={9} title={get('@PatientInfo.smileDesign.restorativePhase.optionalAppliances')} value={data.optionalAppliances} get={get} />
                    <ReadOnlyQuestion number={10} title={get('@PatientInfo.smileDesign.restorativePhase.remark')} value={data.restorationRemark} get={get} />
                </>
            )}
            {isGuided && (
                <>
                    <div className="restorative-product-heading">
                        {get('@PatientInfo.smileDesign.restorativePhase.ceramicVeneersGuidedDesc')}
                    </div>
                    <ReadOnlyQuestion number={2} title={get('@PatientInfo.smileDesign.restorativePhase.materialChoice')} value={material} get={get} />
                    <div className="restorative-question-title">3. {get('@PatientInfo.smileDesign.restorativePhase.shade')}</div>
                    {material && <><ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.currentToothShade')} value={data.currentShade} get={get} /><ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.desiredFinalShade')} value={data.targetShade} get={get} /></>}
                    <ReadOnlyQuestion number={4} title={get('@PatientInfo.smileDesign.restorativePhase.translucencyLevel')} value={data.translucencyLevel} get={get} />
                    <ReadOnlyQuestion number={5} title={get('@PatientInfo.smileDesign.restorativePhase.surfaceTexture')} value={data.surfaceTexture} get={get} />
                    <ReadOnlyQuestion number={6} title={get('@PatientInfo.smileDesign.restorativePhase.surfaceFinishing')} value={data.surfaceFinishingCharacterization} get={get} />
                    {data.surfaceFinishingCharacterization === 'stainingGlazing' && <ReadOnlyQuestion title={get('@PatientInfo.smileDesign.restorativePhase.effectsToReproduce')} value={data.effectsToReproduce} get={get} />}
                    <ReadOnlyQuestion number={7} title={get('@PatientInfo.smileDesign.restorativePhase.designSpecifications')} value={data.designSpecifications} get={get} />
                    <ReadOnlyQuestion number={8} title={get('@PatientInfo.smileDesign.restorativePhase.optionalAppliances')} value={data.optionalAppliances} get={get} />
                    <ReadOnlyQuestion number={9} title={get('@PatientInfo.smileDesign.restorativePhase.remark')} value={data.restorationRemark} get={get} />
                </>
            )}
        </>
    );
};

/** Renders Prescription Notes in editable or read-only mode. */
const PrescriptionNoteStep = ({
    get,
    q1Options = [],
    onRestorationSolutionChange,
    RadioCardGroup,
    PrescriptionDetails,
    FieldError,
    readOnly = false,
    data = {},
}) => {
    const languageGet = useLang();
    const translate = get || languageGet;
    const solution = q1Options.find((option) => option.value === data.plannedRestorationSolution);
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);
    if (readOnly) {
        return (
            <div className={styles.content}>
                {
                    displayFirstFitFlag && <div className="restorative-section-heading">{translate('@PatientInfo.smileDesign.restorativePhase.sectionOne')}</div>
                }
                <div className="restorative-question-title">1. {translate('@PatientInfo.smileDesign.restorativePhase.plannedRestorationSolution')}</div>
                <div className="restorative-readonly-value">{solution ? translate(solution.label) : formatValue(data.plannedRestorationSolution, translate)}</div>
                <PrescriptionReadOnlyDetails data={data} get={translate} />
            </div>
        );
    }

    return (
        <div className={styles.content}>
            <div className={styles.heading}>{translate('@PatientInfo.smileDesign.restorativePhase.prescriptionTitle')}</div>
            <div className={styles.subheading}>{translate('@PatientInfo.smileDesign.restorativePhase.stepThree')}</div>
            <div
                className={styles.description}
                dangerouslySetInnerHTML={{
                    __html: translate('@PatientInfo.smileDesign.restorativePhase.prescriptionDescription'),
                }}
            />
            {
                displayFirstFitFlag && <div className="restorative-section-heading">{translate('@PatientInfo.smileDesign.restorativePhase.sectionOne')}</div>
            }
            <div className="restorative-question-title">
                1. {translate('@PatientInfo.smileDesign.restorativePhase.plannedRestorationSolution')} <b>*</b>
            </div>
            <RadioCardGroup
                name="plannedRestorationSolution"
                required
                onValueChange={onRestorationSolutionChange}
                className="restorative-planned-solution-grid"
                options={q1Options.map((option) => ({
                    value: option.value,
                    label: translate(option.label),
                    description: translate(option.desc),
                    disabled: option.disable,
                }))}
            />
            <FieldError name="plannedRestorationSolution" />
            <PrescriptionDetails get={translate} />
        </div>
    );
};

export default PrescriptionNoteStep;
